import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { UsersService } from '../../services/users.service';
import { GlobalsService } from '../../services/globals.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { VendorCreateLoginDialogComponent } from './vendor-create-login-dialog.component';
import { BrandOnboardDialogComponent } from './brand-onboard-dialog.component';
import type { ExistingLoginOption } from './vendor-create-login-dialog.component';
import type { VendorLocation, VendorRow, VendorUserGroupOption } from './vendors.model';

@Component({
	selector: 'app-vendors',
	templateUrl: './vendors.component.html',
	styleUrls: ['./vendors.component.scss']
})
export class VendorsComponent implements OnInit {
	title = 'Vendors';
	color = '';
	loading = false;
	preparingDialog = false;
	rows: VendorRow[] = [];
	search = new FormControl('');
	filterCtrl = new FormControl<'all' | 'needs_login' | 'has_login'>('all');

	constructor(
		private usersService: UsersService,
		private globalsService: GlobalsService,
		private globalSearchService: GlobalSearchService,
		private router: Router,
		private dialog: MatDialog
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.refresh();
	}

	get total(): number {
		return this.rows.length;
	}

	get needsCount(): number {
		return this.rows.filter((r) => !r.has_login).length;
	}

	get filteredRows(): VendorRow[] {
		const f = this.filterCtrl.value || 'all';
		const term = (this.search.value || '').trim().toLowerCase();
		return this.rows.filter((r) => {
			if (f === 'needs_login' && r.has_login) { return false; }
			if (f === 'has_login' && !r.has_login) { return false; }
			if (term) {
				const hay = `${r.brand_name} ${r.supplier_id} ${r.supplier_name}`.toLowerCase();
				if (!hay.includes(term)) { return false; }
			}
			return true;
		});
	}

	refresh(): void {
		this.loading = true;
		this.usersService.getVendors().subscribe({
			next: (rows: any) => {
				this.rows = (Array.isArray(rows) ? rows : []).map((r: any) => ({
					manufacturer_id: Number(r.manufacturer_id),
					brand_name: r.brand_name || '',
					supplier_id: r.supplier_id || '',
					supplier_name: r.supplier_name || '',
					logins: Array.isArray(r.logins) ? r.logins : [],
					has_login: !!r.has_login,
					suggested_salesmancode: (r.suggested_salesmancode || '').toString(),
					suggested_userid: (r.suggested_userid || '').toString(),
				})).filter((r: VendorRow) => r.manufacturer_id > 0);
				this.loading = false;
			},
			error: () => {
				this.loading = false;
				this.globalSearchService.showNotification('Could not load vendors', 'danger', 'bottom', 'right');
			}
		});
	}

	openCreateDialog(row: VendorRow): void {
		this.preparingDialog = true;
		forkJoin({
			groups: this.usersService.getUserGroups(),
			locations: this.globalsService.getAllLocations(),
			existingLogins: this.usersService.getExistingLogins(),
		}).subscribe({
			next: ({ groups, locations, existingLogins }) => {
				this.preparingDialog = false;
				const ref = this.dialog.open(VendorCreateLoginDialogComponent, {
					width: 'min(480px, 100vw - 32px)',
					maxWidth: '95vw',
					autoFocus: 'first-tabbing-element',
					restoreFocus: true,
					data: {
						row,
						usergroups: this.normalizeUserGroups(groups),
						locations: this.normalizeLocations(locations),
						supplierBrands: this.brandsForSupplier(row),
						existingLogins: this.normalizeExistingLogins(existingLogins),
					},
				});
				ref.afterClosed().subscribe((result) => {
					if (result?.created) {
						this.globalSearchService.showNotification(
							`${row.brand_name} login created`, 'success', 'bottom', 'right'
						);
						this.refresh();
					}
				});
			},
			error: () => {
				this.preparingDialog = false;
				this.globalSearchService.showNotification(
					'Could not load user groups or locations for the form.', 'danger', 'bottom', 'right'
				);
			},
		});
	}

	/**
	 * Add-a-brand entry point: creates/links the brand + vendor together (closing the
	 * old 3-screen gap), then hands straight into the same "create login" dialog used
	 * for any other row — one continuous flow instead of three separate screens.
	 */
	openAddBrandDialog(): void {
		const ref = this.dialog.open(BrandOnboardDialogComponent, {
			width: 'min(520px, 100vw - 32px)',
			maxWidth: '95vw',
			autoFocus: 'first-tabbing-element',
			restoreFocus: true,
			data: {},
		});
		ref.afterClosed().subscribe((result) => {
			if (result?.created && result.row) {
				this.globalSearchService.showNotification(
					`${result.row.brand_name} added — now set up its login`, 'success', 'bottom', 'right'
				);
				this.rows = [...this.rows, result.row];
				this.openCreateDialog(result.row);
			}
		});
	}

	openEdit(userid: string): void {
		this.router.navigate(['/users/edit', userid]);
	}

	/** Reversible: flips www_users.blocked. No data is removed. */
	toggleLogin(l: VendorRow['logins'][number]): void {
		const next: 0 | 1 = l.blocked ? 0 : 1;
		const verb = next ? 'Block' : 'Unblock';
		if (!confirm(`${verb} login "${l.userid}"?`)) {
			return;
		}
		this.usersService.toggleLogin(l.userid, next).subscribe({
			next: (res: any) => {
				if (res?.success) {
					this.globalSearchService.showNotification(`${l.userid} ${verb.toLowerCase()}ed`, 'success', 'bottom', 'right');
					this.refresh();
				} else {
					this.globalSearchService.showNotification(res?.message || 'Could not update login', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.globalSearchService.showNotification(err?.error?.message || 'Could not update login', 'danger', 'bottom', 'right');
			},
		});
	}

	/** Permanent — refused server-side for shared/house-scope codes. */
	deleteLogin(l: VendorRow['logins'][number]): void {
		if (!l.dedicated) {
			this.globalSearchService.showNotification(
				'Shared/house scope login — remove it from Users instead.', 'danger', 'bottom', 'right'
			);
			return;
		}
		if (!confirm(`Permanently delete login "${l.userid}" (code ${l.salesmancode})? This cannot be undone.`)) {
			return;
		}
		this.usersService.deleteLogin(l.userid).subscribe({
			next: (res: any) => {
				if (res?.success) {
					this.globalSearchService.showNotification(`${l.userid} deleted`, 'success', 'bottom', 'right');
					this.refresh();
				} else {
					this.globalSearchService.showNotification(res?.message || 'Could not delete login', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.globalSearchService.showNotification(err?.error?.message || 'Could not delete login', 'danger', 'bottom', 'right');
			},
		});
	}

	/** Undo Add Brand. Refused server-side once the brand has a dedicated login. */
	unlinkBrand(row: VendorRow): void {
		if (!confirm(`Unlink "${row.brand_name}" from ${row.supplier_name} (${row.supplier_id})? It drops off this list until re-added.`)) {
			return;
		}
		this.usersService.unlinkBrand(row.manufacturer_id).subscribe({
			next: (res: any) => {
				if (res?.success) {
					this.globalSearchService.showNotification(`${row.brand_name} unlinked`, 'success', 'bottom', 'right');
					this.refresh();
				} else {
					this.globalSearchService.showNotification(res?.message || 'Could not unlink brand', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.globalSearchService.showNotification(err?.error?.message || 'Could not unlink brand', 'danger', 'bottom', 'right');
			},
		});
	}

	/** Brands under the same supplier as this row, clicked brand first, so one login can scope to all of them. */
	private brandsForSupplier(row: VendorRow): { manufacturer_id: number; brand_name: string }[] {
		const sup = (row.supplier_id || '').trim();
		const siblings = this.rows
			.filter((r) => (r.supplier_id || '').trim() === sup && r.manufacturer_id !== row.manufacturer_id)
			.map((r) => ({ manufacturer_id: r.manufacturer_id, brand_name: r.brand_name }))
			.sort((a, b) => a.brand_name.localeCompare(b.brand_name));
		return [{ manufacturer_id: row.manufacturer_id, brand_name: row.brand_name }, ...siblings];
	}

	private normalizeUserGroups(raw: unknown): VendorUserGroupOption[] {
		const arr = Array.isArray(raw) ? raw : [];
		return [...arr]
			.map((g: any) => ({
				user_group_id: g.user_group_id,
				name: String(g.name ?? ''),
				user_count: g.user_count != null ? Number(g.user_count) : undefined,
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
	}

	private normalizeExistingLogins(raw: unknown): ExistingLoginOption[] {
		const arr = Array.isArray(raw) ? raw : [];
		return [...arr]
			.map((l: any) => ({
				userid: String(l.userid ?? '').trim(),
				salesmancode: String(l.salesmancode ?? '').trim(),
				realname: String(l.realname ?? ''),
				blocked: !!Number(l.blocked),
			}))
			.filter((l) => l.userid !== '')
			.sort((a, b) => a.userid.localeCompare(b.userid));
	}

	private normalizeLocations(raw: unknown): VendorLocation[] {
		const arr = Array.isArray(raw) ? raw : [];
		return [...arr]
			.map((l: any) => ({
				loccode: String(l.loccode ?? '').trim(),
				locationname: l.locationname != null ? String(l.locationname) : undefined,
				orignalname: l.orignalname != null ? String(l.orignalname) : undefined,
			}))
			.filter((l) => l.loccode !== '')
			.sort((a, b) => a.loccode.localeCompare(b.loccode));
	}
}
